import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval
} from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Rectangle,
  ResponsiveContainer
} from 'recharts';
import { breakpoints } from 'styles';
import { useReduxState } from 'hooks';
import { FlexContainer, LoadingIndicator, Tooltip } from 'components/common';
import { DropdownSelectable } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';
import { StatItem } from 'components/common/stats';
import { MyTeamActivityChartLabel } from '.';

import type { ActivityType } from 'shared-types';
import type { GetTeamActivitiesRes } from 'shared-types/responses';
import type { ActivityTypeWithAll } from './MyTeamActivity';

const CustomCursor = ({
  x,
  y,
  width,
  height,
  fill
}: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill: string;
}) => <Rectangle fill={fill} x={x} y={y} width={width} height={height} />;

const CustomTooltip = ({
  active,
  colors,
  columnNames,
  payload,
  label,
  theme
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <div className="date">{label}</div>
        <div className="items">
          {payload
            .reverse()
            .map((item) =>
              item.value > 0 ? (
                <MyTeamActivityChartLabel
                  count={item.value}
                  key={item.dataKey}
                  mappedColors={colors}
                  mappedNames={columnNames}
                  type={item.dataKey}
                />
              ) : null
            )}
        </div>

        <style jsx>{`
          .custom-tooltip {
            background-color: white;
            border-radius: ${theme.dropdownBorderRadius};
            box-shadow: 0 0 4px ${theme.lightGreyDarkActive};
            color: ${theme.textColorLighter};
            display: flex;
            flex-direction: column;
            padding: 8px 10px 5px 10px;
          }
          .date {
            font-size: 1.4rem;
            font-weight: 600;
            margin: 0;
            padding: 0;
          }
          .items {
            display: flex;
            flex-direction: column;
            gap: 5px;
            margin-top: 5px;
          }
          .items > p:first-child {
            margin-top: 10px;
          }
          .items:last-child {
            margin-bottom: 5px;
          }
          .item {
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  return null;
};

interface Props {
  colorsByActivityType: { [key in ActivityType]: string };
  data: GetTeamActivitiesRes['activitiesData'];
  fromDate: string;
  loading: boolean;
  namesByActivityType: {
    [key in ActivityType]: { message: string; placeholder: string };
  };
  numberOfActivitiesByType: GetTeamActivitiesRes['numberOfActivitiesByType'];
  onActivityTypeChange: (type: ActivityType) => void;
  range: 'day' | 'week' | 'month';
  selectedActivityType: ActivityTypeWithAll;
  toDate: string;
  totalWorkloadInHours: number;
}

const MyTeamActivityChart = ({
  colorsByActivityType,
  data,
  fromDate,
  loading,
  namesByActivityType,
  numberOfActivitiesByType,
  onActivityTypeChange,
  range,
  selectedActivityType,
  toDate,
  totalWorkloadInHours
}: Props) => {
  const {
    messages,
    serverConfig: {
      individualAchievementsEnabled,
      hideAchievementsFromUI,
      hideCalendarEventsFromUI,
      hideCoursesFromUI,
      hidePathsFromUI
    },
    theme
  } = useReduxState();

  const activityTypes = [
    'achievement',
    'calendar-event',
    'competency',
    'course',
    'individual-achievement',
    'path'
  ];

  const interval = { start: Date.parse(fromDate), end: Date.parse(toDate) };

  const dates =
    range === 'day'
      ? eachDayOfInterval(interval)
      : range === 'week'
      ? eachWeekOfInterval(interval, { weekStartsOn: 1 })
      : eachMonthOfInterval(interval);

  const xAxisStep = Math.min(Math.floor(dates.length * 0.1), 5);

  const useRadius = (activityType, propsData) => {
    for (let i = activityTypes.length - 1; i >= 0; i--) {
      if (propsData[activityTypes[i]]) return activityType === activityTypes[i];
    }
    return false;
  };

  return (
    <div className="activity-chart">
      <div className="header">
        <div className="header-main">
          <div className="header-stats">
            <StatItem
              align="left"
              horizontal={true}
              label={messages.myTeamActivities}
              value={numberOfActivitiesByType?.all || 0}
            />

            <StatItem
              align="left"
              horizontal={true}
              label={
                <Tooltip content={messages.myTeamActivityWorkloadInfo}>
                  <FlexContainer style={{ gap: 5 }}>
                    {messages.commonTotalWorkloadInHours}
                    <Icon icon="question-circle" />
                  </FlexContainer>
                </Tooltip>
              }
              value={totalWorkloadInHours || 0}
            />
          </div>

          <DropdownSelectable
            category="lighter"
            defaultValue="all"
            onChange={onActivityTypeChange}
            options={[
              { text: messages.commonAll, value: 'all' },
              ...[
                ...(hideCoursesFromUI
                  ? []
                  : [{ text: messages.commonCourses, value: 'course' }]),
                ...(hidePathsFromUI
                  ? []
                  : [{ text: messages.commonPaths, value: 'path' }]),
                ...(hideAchievementsFromUI
                  ? []
                  : [
                      {
                        text: messages.commonAchievementsAlt,
                        value: 'achievement'
                      }
                    ]),
                ...(hideCalendarEventsFromUI
                  ? []
                  : [
                      {
                        text: messages.commonCalendarEvents,
                        value: 'calendar-event'
                      }
                    ]),
                { text: messages.analyticsCompetencies, value: 'competency' },
                ...(individualAchievementsEnabled && !hideAchievementsFromUI
                  ? [
                      {
                        text: messages.individualAchievements,
                        value: 'individual-achievement'
                      }
                    ]
                  : [])
              ].sort((a, b) => (a.text > b.text ? 1 : -1))
            ]}
            showSelectedText={true}
            value={selectedActivityType}
          />
        </div>

        {loading ? null : (
          <div className="labels">
            <MyTeamActivityChartLabel
              count={numberOfActivitiesByType?.course}
              mappedColors={colorsByActivityType}
              mappedNames={namesByActivityType}
              type="course"
            />
            <MyTeamActivityChartLabel
              count={numberOfActivitiesByType?.path}
              mappedColors={colorsByActivityType}
              mappedNames={namesByActivityType}
              type="path"
            />
            <MyTeamActivityChartLabel
              count={numberOfActivitiesByType?.achievement}
              mappedColors={colorsByActivityType}
              mappedNames={namesByActivityType}
              type="achievement"
            />
            <MyTeamActivityChartLabel
              count={numberOfActivitiesByType?.['individual-achievement']}
              mappedColors={colorsByActivityType}
              mappedNames={namesByActivityType}
              type="individual-achievement"
            />
            <MyTeamActivityChartLabel
              count={numberOfActivitiesByType?.['calendar-event']}
              mappedColors={colorsByActivityType}
              mappedNames={namesByActivityType}
              type="calendar-event"
            />
            <MyTeamActivityChartLabel
              count={numberOfActivitiesByType?.competency}
              mappedColors={colorsByActivityType}
              mappedNames={namesByActivityType}
              type="competency"
            />
          </div>
        )}
      </div>

      {loading ? (
        <LoadingIndicator />
      ) : (
        <div className="chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20 }}>
              <CartesianGrid
                stroke={theme.lightGreyDarkActive}
                strokeDasharray="5 7"
                vertical={false}
              />

              <XAxis
                allowDataOverflow={true}
                axisLine={false}
                dataKey="label"
                dy={10}
                interval={xAxisStep}
                stroke={theme.textColorLighter}
                tickLine={false}
                {...{ fontSize: 14, fontWeight: 600 }}
              />
              <YAxis
                allowDecimals={false}
                axisLine={false}
                domain={[0, 'auto']}
                dx={-10}
                stroke={theme.textColorLighter}
                tickLine={false}
                {...{ fontSize: 14, fontWeight: 600 }}
              />

              <RechartsTooltip
                content={({ active, payload, label }) => (
                  <CustomTooltip
                    active={active}
                    colors={colorsByActivityType}
                    columnNames={namesByActivityType}
                    payload={payload}
                    label={label}
                    theme={theme}
                  />
                )}
                cursor={<CustomCursor fill={theme.lightGrey} />}
                wrapperStyle={{ fontSize: '1.4rem' }}
              />

              {activityTypes.map((category, i) => {
                return (
                  <Bar
                    barSize={8}
                    dataKey={category}
                    key={category}
                    name={namesByActivityType[category]}
                    stackId="1"
                    fill={colorsByActivityType[category]}
                    shape={(props) => {
                      // we want to show a radius only for the top section of the bar
                      if (useRadius(category, props)) {
                        props.radius = [4, 4, 0, 0];
                      }

                      return <Rectangle {...props} />;
                    }}
                  />
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <style jsx>{`
        .header {
          padding: 15px 15px 0px;
        }
        .header-main {
          display: flex;
          justify-content: space-between;
          gap: 50px;
        }
        .header-stats {
          display: flex;
          gap: 50px;
        }
        .labels {
          display: flex;
          flex-wrap: wrap;
          gap: 10px 50px;
          margin-top: 20px;
        }
        .label-indicator {
          border-radius: 20px;
          display: inline-block;
          margin-right: 7px;
          height: 11px;
          width: 11px;
        }
        .label-stat {
          display: inline-block;
        }
        .chart {
          height: 280px;
          margin: 10px 15px 15px -20px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .header {
            padding: 20px 20px 0px;
          }
          .chart {
            margin: 15px 20px 20px -15px;
          }
        }
      `}</style>
    </div>
  );
};

export default MyTeamActivityChart;
